import { RequestAssertion } from '../types/assertions';

/**
 * Identity for de-duplicating merged request + data-set lists. Omits request/dataSet ids
 * so the same logical assertion stored once at request level and again on a data set
 * collapses to a single row (data-set row wins when both exist).
 *
 * expectedValue and HARD/SOFT are NOT part of identity — only one row per logical assertion
 * (e.g. one Status Code, one Content-Type, one Headers+name, one JSON Path+path).
 */
export function assertionLogicalIdentityKey(a: RequestAssertion): string {
  const type = (a.assertionKey ?? '').trim().toLowerCase();
  const path = (a.jsonPath ?? '').trim();
  const header = (a.headerName ?? '').trim().toLowerCase();
  const op = (a.validationOperator ?? '').trim().toLowerCase();
  if (type === 'headers' || header) {
    return `${type}|header:${header}|${op}`;
  }
  if (type === 'json_path') {
    return `${type}|path:${path}|${op}`;
  }
  return `${type}|${op}`;
}

function dedupeById(list: RequestAssertion[]): RequestAssertion[] {
  const seen = new Set<number>();
  const out: RequestAssertion[] = [];
  for (const a of list) {
    if (a.id != null) {
      if (seen.has(a.id)) continue;
      seen.add(a.id);
    }
    out.push(a);
  }
  return out;
}

/** Request-level + data-set assertions (dedupe by id, then by logical identity).
 * Prefer the newest updatedAt so an edited copy is not shadowed by a stale twin after refresh.
 * When timestamps tie, data-set rows still win over request-level.
 */
export function mergeRequestAndDataSetAssertions(
  requestLevel: RequestAssertion[],
  dataSetSpecific: RequestAssertion[]
): RequestAssertion[] {
  const req = dedupeById(requestLevel);
  const ds = dedupeById(dataSetSpecific);
  const byLogical = new Map<string, RequestAssertion>();

  const assertionUpdatedAtMs = (a: RequestAssertion): number => {
    if (!a.updatedAt) return 0;
    const ms = Date.parse(String(a.updatedAt));
    return Number.isFinite(ms) ? ms : 0;
  };

  const preferAssertion = (incoming: RequestAssertion, existing: RequestAssertion): RequestAssertion => {
    const incomingMs = assertionUpdatedAtMs(incoming);
    const existingMs = assertionUpdatedAtMs(existing);
    if (incomingMs !== existingMs) {
      return incomingMs > existingMs ? incoming : existing;
    }
    const incomingIsDataSet = incoming.dataSetId != null && incoming.dataSetId > 0;
    const existingIsDataSet = existing.dataSetId != null && existing.dataSetId > 0;
    if (incomingIsDataSet !== existingIsDataSet) {
      return incomingIsDataSet ? incoming : existing;
    }
    return incoming;
  };

  for (const a of req) {
    const key = assertionLogicalIdentityKey(a);
    const existing = byLogical.get(key);
    byLogical.set(key, existing ? preferAssertion(a, existing) : a);
  }
  for (const a of ds) {
    const key = assertionLogicalIdentityKey(a);
    const existing = byLogical.get(key);
    byLogical.set(key, existing ? preferAssertion(a, existing) : a);
  }
  return Array.from(byLogical.values());
}

/** Collapse duplicate rows from validate* APIs when request- and data-set-level assertions overlap. */
export function dedupeValidationResults(results: RequestAssertion[]): RequestAssertion[] {
  const persistedOnly = results.filter((r) => r.id == null || r.id > 0);
  const seenIds = new Set<number>();
  const byLogical = new Map<string, RequestAssertion>();

  for (const a of persistedOnly) {
    if (a.id != null && a.id > 0) {
      if (seenIds.has(a.id)) continue;
      seenIds.add(a.id);
    }

    const key = assertionLogicalIdentityKey(a);
    const existing = byLogical.get(key);
    if (!existing) {
      byLogical.set(key, a);
      continue;
    }
    const aIsDataSet = a.dataSetId != null && a.dataSetId > 0;
    const existingIsDataSet = existing.dataSetId != null && existing.dataSetId > 0;
    if (aIsDataSet && !existingIsDataSet) {
      byLogical.set(key, a);
    }
  }

  return Array.from(byLogical.values());
}

/** Hide request-level type checks when the same jsonPath has a literal equals assertion (e.g. $.lastName). */
export function filterRedundantJsonPathTypeAssertions(list: RequestAssertion[]): RequestAssertion[] {
  const equalsPaths = new Set<string>();
  for (const a of list) {
    if (a.assertionKey !== 'json_path' || !a.jsonPath?.trim()) continue;
    const op = (a.validationOperator ?? 'equals').trim().toLowerCase();
    if (op === 'equals' || op === 'not_equals') {
      equalsPaths.add(a.jsonPath.trim());
    }
  }
  if (equalsPaths.size === 0) return list;

  return list.filter((a) => {
    if (a.assertionKey !== 'json_path' || !a.jsonPath?.trim()) return true;
    const op = (a.validationOperator ?? '').trim().toLowerCase();
    if (op === 'equals' || op === 'not_equals') return true;
    if (
      ['is_integer', 'is_string', 'is_email', 'is_url', 'is_not_empty', 'is_present'].includes(op)
    ) {
      return !equalsPaths.has(a.jsonPath.trim());
    }
    return true;
  });
}
